import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  Card,
  Button,
  CircularProgress,
} from '@mui/material';
import PaidIcon from '@mui/icons-material/Paid';
import PendingIcon from '@mui/icons-material/Pending';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import TakeoutDiningIcon from '@mui/icons-material/TakeoutDining';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { formatCurrency } from '../../../app/utils/formatters';
import OrderStatusLabel from '../../../app/components/OrderStatusLabel';
import { Order } from '../../../app/data/models/orders/order';
import { getOrderTypeChipProps } from '../../../app/utils/orderUtils';
import { OrderType } from '../../../app/data/models/orders/orderType';
import { useRestaurant } from '@app/hooks/useRestaurant';

interface OrderDetailsProps {
  order: Order | null;
  restaurantId: string;
}

const OrderDetails: React.FC<OrderDetailsProps> = ({ order, restaurantId }) => {
  const { t } = useTranslation();
  const { restaurant } = useRestaurant(restaurantId);
  const [itemsError, setItemsError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    if (order) {
      // Reset error state when order changes
      setItemsError(null);
      setIsRetrying(false);
    }
  }, [order]);

  const handleRetry = () => {
    setItemsError(null);
    setIsRetrying(true);

    // Simulate a retry attempt
    // In a real implementation, this would trigger a re-fetch of the order details
    setTimeout(() => {
      // If we still don't have order items after retry, show error
      if (order && (!order.subOrders || order.subOrders.length === 0)) {
        setItemsError(t('biller.error-loading-items'));
      }
      setIsRetrying(false);
    }, 1500);
  };

  if (!order) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary" variant="h6">
          {t('biller.select-order-prompt')}
        </Typography>
      </Box>
    );
  }

  // Helper function to get payment status
  const getPaymentStatus = (order: Order) => {
    // Check if order has been paid
    const isPaid = order.isPaid;

    return {
      text: isPaid ? 'Paid' : 'Pending Payment',
      icon: isPaid ? (
        <PaidIcon fontSize="small" />
      ) : (
        <PendingIcon fontSize="small" />
      ),
      color: isPaid ? ('success' as const) : ('warning' as const),
    };
  };

  const paymentStatus = getPaymentStatus(order);

  return (
    <Box sx={{ pb: { xs: 1, sm: 2 } }}>
      <Grid container spacing={{ xs: 1, sm: 2 }}>
        {/* Order Header */}
        <Grid item xs={12}>
          <Card sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Box
              display="flex"
              flexDirection={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              flexWrap="wrap"
              gap={2}
            >
              <Box>
                <Typography variant="h5" gutterBottom>
                  {t('biller.order-number')}: #{order.tokenNumber}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {order.orderDate && format(new Date(order.orderDate), 'PPpp')}
                </Typography>
              </Box>
              <Chip
                icon={paymentStatus.icon}
                label={t(
                  `biller.${paymentStatus.text.toLowerCase().replace(' ', '-')}`
                )}
                size="small"
                color={paymentStatus.color}
                variant="outlined"
                sx={{
                  borderRadius: '16px',
                }}
              />
            </Box>
          </Card>
        </Grid>

        {/* Order Info */}
        <Grid item xs={12}>
          <Card sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Typography
              variant="h6"
              gutterBottom
              color="text.primary"
              sx={{ mb: { xs: 1, sm: 2 } }}
            >
              {t('biller.order-details')}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: { xs: 1, sm: 2 },
                flexWrap: 'wrap',
                pb: { xs: 1, sm: 2 },
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <OrderStatusLabel orderStatus={order.orderStatus} />
              <Chip
                {...getOrderTypeChipProps(order.orderType)}
                label={t(getOrderTypeChipProps(order.orderType).label)}
                size="small"
                variant="outlined"
                sx={{
                  height: '24px',
                  borderRadius: '16px',
                }}
              />
              {/* Table number for Dine-In, Customer name/phone for Take-Away */}
              {order.orderType === OrderType.DineIn && (
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
                  <RestaurantIcon
                    sx={{ fontSize: 18, color: 'success.main', mr: 0.5 }}
                  />
                  <Typography
                    variant="body2"
                    color="success.main"
                    fontWeight="bold"
                    sx={{
                      fontSize: { xs: '0.92rem', sm: '1.02rem' },
                      letterSpacing: 0.2,
                    }}
                  >
                    {t('biller.table')}{' '}
                    {order.tableNumber
                      ? order.tableNumber
                      : t('biller.no-table')}
                  </Typography>
                </Box>
              )}
              {order.orderType === OrderType.TakeAway && (
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
                  <TakeoutDiningIcon
                    sx={{ fontSize: 18, color: 'warning.main', mr: 0.5 }}
                  />
                  <Typography
                    variant="body2"
                    color="warning.main"
                    fontWeight="bold"
                    sx={{
                      fontSize: { xs: '0.92rem', sm: '1.02rem' },
                      letterSpacing: 0.2,
                    }}
                  >
                    {order.customerName || t('biller.walk-in-customer')}
                    {order.customerPhone ? ` (${order.customerPhone})` : ''}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Order Items Table */}
            <Box sx={{ overflowX: 'auto' }}>
              {/* Show loading spinner when order exists but subOrders are not loaded yet */}
              {isRetrying ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress size={40} thickness={4} />
                </Box>
              ) : !order.subOrders || order.subOrders.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress size={40} thickness={4} />
                </Box>
              ) : itemsError ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    my: 4,
                  }}
                >
                  <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                    {itemsError}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleRetry}
                    size="small"
                  >
                    {t('common.retry')}
                  </Button>
                </Box>
              ) : order.subOrders &&
                // Only show "No items" message if we have subOrders array with at least one item
                // This prevents showing the message during initial loading
                order.subOrders.length > 0 &&
                order.subOrders.every(
                  (subOrder) =>
                    !subOrder.orderItems || subOrder.orderItems.length === 0
                ) ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  sx={{ my: 2 }}
                >
                  {t('biller.no-items-in-order')}
                </Typography>
              ) : (
                <TableContainer component={Paper} sx={{ mb: 2, minWidth: 600 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('biller.item')}</TableCell>
                        <TableCell align="center">
                          {t('biller.quantity')}
                        </TableCell>
                        <TableCell align="right">{t('biller.price')}</TableCell>
                        <TableCell align="right">{t('biller.total')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {order.subOrders &&
                        order.subOrders
                          .filter((subOrder) => subOrder.orderItems?.length > 0)
                          .map((subOrder) =>
                            subOrder.orderItems.map((item, index) => (
                              <React.Fragment key={`${item.id}-${index}`}>
                                <TableRow>
                                  <TableCell
                                    component="th"
                                    scope="row"
                                    sx={{ verticalAlign: 'top' }}
                                  >
                                    <Typography variant="body2">
                                      {item.name}
                                    </Typography>
                                    {/* Customizations */}
                                    {item.customizations &&
                                      item.customizations.length > 0 && (
                                        <Box sx={{ mt: 1, pl: 2 }}>
                                          {item.customizations.map(
                                            (customization) => (
                                              <Box
                                                key={customization.id}
                                                sx={{ mb: 0.5 }}
                                              >
                                                <Typography
                                                  variant="body2"
                                                  color="text.secondary"
                                                >
                                                  {
                                                    customization.customerInstruction
                                                  }
                                                  :
                                                </Typography>
                                                <Typography
                                                  variant="body2"
                                                  color="text.secondary"
                                                  sx={{ pl: 1 }}
                                                >
                                                  {customization.options
                                                    .filter(
                                                      (option) =>
                                                        option.quantity > 0
                                                    )
                                                    .map((option, idx, arr) => (
                                                      <span key={option.id}>
                                                        {option.quantity}x{' '}
                                                        {option.name}
                                                        {option.price > 0 &&
                                                          ` (+${formatCurrency(option.price)})`}
                                                        {idx < arr.length - 1
                                                          ? ', '
                                                          : ''}
                                                      </span>
                                                    ))}
                                                </Typography>
                                              </Box>
                                            )
                                          )}
                                        </Box>
                                      )}
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{ verticalAlign: 'top' }}
                                  >
                                    {item.quantity}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{ verticalAlign: 'top' }}
                                  >
                                    {item.price
                                      ? formatCurrency(item.price)
                                      : '-'}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{ verticalAlign: 'top' }}
                                  >
                                    {item.price
                                      ? formatCurrency(
                                          item.quantity * item.price
                                        )
                                      : '-'}
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            ))
                          )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            {/* Order Summary */}
            <Box
              display="flex"
              justifyContent={{ xs: 'center', sm: 'flex-end' }}
            >
              <Table sx={{ maxWidth: { xs: '100%', sm: 400 } }}>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body1" color="text.secondary">
                        {t('biller.subtotal')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(order.subtotal)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body1" color="text.secondary">
                        {t('biller.tax')}{' '}
                        {restaurant?.settings?.priceIncludesTax &&
                          `(${t('biller.tax-inclusive')})`}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(order.taxAmount)}
                    </TableCell>
                  </TableRow>
                  {order.deliveryFee > 0 && (
                    <TableRow>
                      <TableCell>
                        <Typography variant="body1" color="text.secondary">
                          {t('biller.delivery-fee')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(order.deliveryFee)}
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell>
                      <Typography variant="h6">{t('biller.total')}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="h6"
                        sx={{ color: 'black', fontWeight: 'bold' }}
                      >
                        {formatCurrency(order.total)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrderDetails;
